import { memo } from 'react';
import configureStore from './configureStore';
import { createSelector } from 'reselect';
import { useSelector } from 'react-redux';
import { createActions, handleActions } from 'redux-actions';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { takeLatest } from 'redux-saga/effects';

const store = configureStore({});

export const dispatchAction = (action) => {
  store.dispatch(action);
}

export const makeDispatcher = (actionCreators) => {
  const result = {};

  Object.keys(actionCreators).forEach((key) => {
    let item = actionCreators[key];
    result[key] = (...args) => {
      dispatchAction(item(...args));
    }
  });

  return result;
};

export const convertUnderscoreUpperCase = (str) => {
  var result = "";

  for (var i = 0; i < str.length; i++) {
    let item = str[i];
    if (item === item.toUpperCase()) {
      result += "_" + item.toUpperCase();
    }
    else {
      result += item.toUpperCase();
    }
  }

  return result;
}
export class MasterReducer {
  constructor(key, initialState, actions, keepState) {
    this.key = key;
    this.actions = actions;
    this.initialState = {
      values: false,
      loading: false,
      loadingMap: [],
      alert: false,
      result: false,
      pagination: {},
      ...initialState,
    }
    this.keepState = keepState;
    this.sagaNames = Object.keys(this.actions).filter(x => !x.includes('Return') && !x.includes('_'));

    this.actionCreators = createActions(
      'SET_STATES',
      'RESET_STATES',
      'ADD_ARRAY_MODEL_ITEMS',
      'UPDATE_ARRAY_MODEL_ITEM',
      'DELETE_ARRAY_MODEL_ITEMS',
      'UPDATE_OBJECT_MODEL_ITEM',
      ...Object.keys(actions).map(x => convertUnderscoreUpperCase(x)),
      {
        prefix: key,
      },
    )

    const actionHandlers = {};
    Object.keys(actions).forEach(key => {
      actionHandlers[this.actionCreators[key]] = actions[key];
    });
    this.reducer = handleActions(
      {
        [this.actionCreators.setStates]: (state, { payload }) => ({
          ...state,
          ...payload,
        }),
        [this.actionCreators.resetStates]: () => (this.initialState),
        [this.actionCreators.addArrayModelItems]: (state, { payload }) => {
          const model = payload?.model;
          if (!model) return state;
          const isPrepend = payload?.isPrepend ?? false;
          const currentItems = state[model] ?? [];
          const toAddItems = payload?.data ?? [];
          return {
            ...state,
            [model]: isPrepend ? [...toAddItems, ...currentItems] : [...currentItems, ...toAddItems],
          }
        },
        [this.actionCreators.updateArrayModelItem]: (state, { payload }) => {
          const model = payload?.model;
          if (!model) return state;
          const currentItems = state[model] ?? [];
          const toUpdateItem = payload?.data ?? {};
          return {
            ...state,
            [model]: currentItems.map(x => x.id === toUpdateItem.id ? toUpdateItem : x),
          }
        },
        [this.actionCreators.deleteArrayModelItems]: (state, { payload }) => {
          const model = payload?.model;
          if (!model) return state;
          const currentItems = state[model] ?? [];
          const toDeleteItems = payload?.data ?? [];
          return {
            ...state,
            [model]: currentItems.filter(x => !toDeleteItems.map(y => y.id).includes(x.id)),
          }
        },
        [this.actionCreators.updateObjectModelItem]: (state, { payload }) => {
          const model = payload?.model;
          if (!model) return state;
          const currentItem = state[model] ?? {};
          const toUpdateItem = payload?.data ?? {};
          return {
            ...state,
            [model]: { ...currentItem, ...toUpdateItem },
          }
        },
        ...actionHandlers,
      },
      this.initialState,
    )

    this.originSelector = (state) => {
      return state[this.key] || this.initialState;
    }

    this.makeSelector = (stateKey) => {
      return createSelector(this.originSelector, state => state[stateKey]);
    }
    this.makeCallbackSelector = (stateKey, callback) => {
      return createSelector(this.originSelector, state => {
        return callback(state[stateKey]);
      });
    }

    this.dispatcher = makeDispatcher(this.actionCreators);

    this.sagaCallback = function* (self) {
      for (const key of self.sagaNames) {
        if (!key.includes('Return') && !key.includes('_')) {
          let reduxAction = self.actionCreators[key];
          let sagaAction = actions['_' + key];
          let returnAction = self.actionCreators[key.replace('_', '') + 'Return'];

          if (sagaAction) {
            yield takeLatest(reduxAction, sagaAction, returnAction, self);
          }
        }
      }
    }

    this.saga = () => {
      return this.sagaCallback(this);
    }

    this.connector = (map) => {
      const mapStateToProps = createStructuredSelector(map);
      const mapDispatchToProps = (dispatch) => {
        return bindActionCreators(this.actionCreators, dispatch);
      }
      return compose(
        connect(mapStateToProps, mapDispatchToProps),
        memo,
      );
    }
  }
};

export const Generator = {
  getActionPayload: (state, payload) => {
    const toSet = {
      loading: payload?.ignoreLoader ? state.loading : true,
    };
    return {
      ...state,
      values: payload,
      result: false,
      alert: false,
      ...toSet,
    }
  },
  getReturnActionPayload: (state, payload) => {
    const toSet = {
      loading: payload?.ignoreLoader ? state.loading : false,
      alert: { type: 'error', code: 'clientError' },
    };

    if (payload?.result) {
      toSet.alert = false;
    }
    else if (payload?.alert) {
      toSet.alert = payload?.alert;
    }
    return {
      ...state,
      result: payload?.result ? payload.result : false,
      successAlert: payload?.result ? payload?.alert : false,
      ...toSet,
    }
  },

  makePureActions: (functionName, sagaCallback, injectActionPayload) => {
    const result = {};
    result[functionName] = (state, { payload }) => {
      return {
        ...Generator.getActionPayload(state, { ...payload, ...injectActionPayload, ignoreLoader: true }),
      }
    };
    result[`${functionName}Return`] = {
      next: (state, { payload }) => {
        return {
          ...Generator.getReturnActionPayload(state, { ...payload, ...injectActionPayload, ignoreLoader: true }),
        }
      },
      throw: (state, { payload }) => {
        return {
          ...Generator.getReturnActionPayload(state, { ...payload, ...injectActionPayload, ignoreLoader: true }),
        }
      },
    };
    result[`_${functionName}`] = sagaCallback;
    return result;
  },
  makeActions: (functionName, sagaCallback, injectActionPayload) => {
    const result = {};
    result[functionName] = (state, { payload }) => {
      const toSet = {
        loadingMap: [...state.loadingMap, functionName],
      }
      return {
        ...Generator.getActionPayload(state, { ...payload, ...injectActionPayload }),
        ...toSet,
      }
    };
    result[`${functionName}Return`] = {
      next: (state, { payload }) => {
        const toSet = {
          loadingMap: state.loadingMap.filter(x => x !== functionName),
        };
        return {
          ...Generator.getReturnActionPayload(state, { ...payload, ...injectActionPayload }),
          ...toSet,
        }
      },
      throw: (state, { payload }) => {
        const toSet = {
          loadingMap: state.loadingMap.filter(x => x !== functionName),
        };
        return {
          ...Generator.getReturnActionPayload(state, { ...payload, ...injectActionPayload }),
          ...toSet,
        }
      },
    };
    result[`_${functionName}`] = sagaCallback;
    return result;
  },
  makeArrayModelActions: (model, functionName, sagaCallback, injectActionPayload) => {
    const result = {};
    result[functionName] = (state, { payload }) => {
      const toSet = {
        loadingMap: [...state.loadingMap, functionName],
      };
      toSet[model] = [];
      return {
        ...Generator.getActionPayload(state, { ...payload, ...injectActionPayload }),
      }
    };
    result[`${functionName}Return`] = {
      next: (state, { payload }) => {
        const currentPagination = state['pagination'] ? state['pagination'] : {};
        const currentPaginationModel = currentPagination[model];
        const toSet = {
          pagination: currentPagination,
          loadingMap: state.loadingMap.filter(x => x !== functionName),
        };
        toSet[model] = payload?.result ? payload.data : [];
        toSet['pagination'][model] = { ...currentPaginationModel, hasMore: true, nextToken: undefined }
        return {
          ...Generator.getReturnActionPayload(state, { ...payload, ...injectActionPayload }),
          ...toSet,
        }
      },
      throw: (state, { payload }) => {
        const toSet = {
          loadingMap: state.loadingMap.filter(x => x !== functionName),
        };
        return {
          ...Generator.getReturnActionPayload(state, { ...payload, ...injectActionPayload }),
          ...toSet,
        }
      },
    };
    result[`_${functionName}`] = sagaCallback;
    return result;
  },
  makePaginationArrayModelActions: (model, functionName, sagaCallback, injectActionPayload) => {
    const result = {};
    result[functionName] = (state, { payload }) => {
      const toSet = {
        loadingMap: [...state.loadingMap, functionName],
      };
      return {
        ...Generator.getActionPayload(state, { ...payload, ...injectActionPayload }),
        ...toSet,
      }
    };
    result[`${functionName}Return`] = {
      next: (state, { payload }) => {
        const currentPagination = state['pagination'] ? state['pagination'] : {};
        const currentPaginationModel = currentPagination[model];
        const currentNextToken = currentPaginationModel.nextToken;
        const currentItems = !payload?.useReset && state[model] && currentNextToken ? state[model] : [];

        const nextToken = !payload?.useReset ? payload?.pagination?.nextToken : undefined;
        const nextTokenId = !payload?.useReset ? payload?.pagination?.nextTokenId : undefined;

        const toSet = {
          pagination: currentPagination,
          loadingMap: state.loadingMap.filter(x => x !== functionName),
        };

        toSet[model] = payload?.result ? [...currentItems, ...payload.data] : [];
        toSet['pagination'][model] = { ...currentPaginationModel, hasMore: payload?.pagination?.hasMore, nextToken: nextToken, nextTokenId: nextTokenId }

        return {
          ...Generator.getReturnActionPayload(state, payload),
          ...toSet,
        }
      },
      throw: (state, { payload }) => {
        const toSet = {
          loadingMap: state.loadingMap.filter(x => x !== functionName),
        };
        return {
          ...Generator.getReturnActionPayload(state, payload),
          ...toSet,
        }
      },
    };
    result[`_${functionName}`] = sagaCallback;
    return result;
  },
  makePrependArrayModelActions: (model, functionName, sagaCallback, injectActionPayload) => {
    const result = {};
    result[functionName] = (state, { payload }) => {
      const toSet = {
        loadingMap: [...state.loadingMap, functionName],
      };
      return {
        ...Generator.getActionPayload(state, { ...payload, ...injectActionPayload }),
        ...toSet,
      }
    };
    result[`${functionName}Return`] = {
      next: (state, { payload }) => {
        const toSet = {
          loadingMap: state.loadingMap.filter(x => x !== functionName),
        };
        const currentItems = state[model] ? state[model] : [];
        toSet[model] = payload?.result ? [...payload.data, ...currentItems] : currentItems;
        return {
          ...Generator.getReturnActionPayload(state, { ...payload, ...injectActionPayload }),
          ...toSet,
        }
      },
      throw: (state, { payload }) => {
        const toSet = {
          loadingMap: state.loadingMap.filter(x => x !== functionName),
        };
        return {
          ...Generator.getReturnActionPayload(state, { ...payload, ...injectActionPayload }),
          ...toSet,
        }
      },
    };
    result[`_${functionName}`] = sagaCallback;
    return result;
  },
  makeDeleteArrayModelActions: (model, functionName, sagaCallback, injectActionPayload) => {
    const result = {};
    result[functionName] = (state, { payload }) => {
      const toSet = {
        loadingMap: [...state.loadingMap, functionName],
      };
      return {
        ...Generator.getActionPayload(state, { ...payload, ...injectActionPayload }),
        ...toSet,
      }
    };
    result[`${functionName}Return`] = {
      next: (state, { payload }) => {
        const toSet = {
          loadingMap: state.loadingMap.filter(x => x !== functionName),
        };
        const currentItems = state[model] ? state[model] : [];
        toSet[model] = payload?.result ? currentItems.filter(x => !payload.data.map(y => y.id).includes(x.id)) : currentItems;
        return {
          ...Generator.getReturnActionPayload(state, { ...payload, ...injectActionPayload }),
          ...toSet,
        }
      },
      throw: (state, { payload }) => {
        const toSet = {
          loadingMap: state.loadingMap.filter(x => x !== functionName),
        };
        return {
          ...Generator.getReturnActionPayload(state, { ...payload, ...injectActionPayload }),
          ...toSet,
        }
      },
    };
    result[`_${functionName}`] = sagaCallback;
    return result;
  },
  makeUpdateArrayModelActions: (model, functionName, sagaCallback, injectActionPayload) => {
    const result = {};
    result[functionName] = (state, { payload }) => {
      const toSet = {
        loadingMap: [...state.loadingMap, functionName],
      };
      return {
        ...Generator.getActionPayload(state, { ...payload, ...injectActionPayload }),
        ...toSet,
      }
    };
    result[`${functionName}Return`] = {
      next: (state, { payload }) => {
        const toSet = {
          loadingMap: state.loadingMap.filter(x => x !== functionName),
        };
        const currentItems = state[model] ? state[model] : [];
        toSet[model] = payload?.result ? currentItems.map(x => x.id === payload.data.id ? payload.data : x) : currentItems;
        return {
          ...Generator.getReturnActionPayload(state, { ...payload, ...injectActionPayload }),
          ...toSet,
        }
      },
      throw: (state, { payload }) => {
        const toSet = {
          loadingMap: state.loadingMap.filter(x => x !== functionName),
        };
        return {
          ...Generator.getReturnActionPayload(state, { ...payload, ...injectActionPayload }),
          ...toSet,
        }
      },
    };
    result[`_${functionName}`] = sagaCallback;
    return result;
  },
  makeObjectModelActions: (model, functionName, sagaCallback, injectActionPayload) => {
    const result = {};
    result[functionName] = (state, { payload }) => {
      const toSet = {
        loadingMap: [...state.loadingMap, functionName],
      };
      toSet[model] = false;
      return {
        ...Generator.getActionPayload(state, { ...payload, ...injectActionPayload }),
      }
    };
    result[`${functionName}Return`] = {
      next: (state, { payload }) => {
        const toSet = {
          loadingMap: state.loadingMap.filter(x => x !== functionName),
        };
        toSet[model] = payload?.result ? payload.data : false;
        return {
          ...Generator.getReturnActionPayload(state, { ...payload, ...injectActionPayload }),
          ...toSet,
        }
      },
      throw: (state, { payload }) => {
        const toSet = {
          loadingMap: state.loadingMap.filter(x => x !== functionName),
        };
        return {
          ...Generator.getReturnActionPayload(state, { ...payload, ...injectActionPayload }),
          ...toSet,
        }
      },
    };
    result[`_${functionName}`] = sagaCallback;
    return result;
  },
  makeUpdateObjectModelActions: (model, functionName, sagaCallback, injectActionPayload) => {
    const result = {};
    result[functionName] = (state, { payload }) => {
      const toSet = {
        loadingMap: [...state.loadingMap, functionName],
      };
      return {
        ...Generator.getActionPayload(state, { ...payload, ...injectActionPayload }),
        ...toSet,
      }
    };
    result[`${functionName}Return`] = {
      next: (state, { payload }) => {
        const toSet = {
          loadingMap: state.loadingMap.filter(x => x !== functionName),
        };
        const currentItem = state[model] ? state[model] : {};
        toSet[model] = payload?.result ? { ...currentItem, ...payload.data } : currentItem;
        return {
          ...Generator.getReturnActionPayload(state, { ...payload, ...injectActionPayload }),
          ...toSet,
        }
      },
      throw: (state, { payload }) => {
        const toSet = {
          loadingMap: state.loadingMap.filter(x => x !== functionName),
        };
        return {
          ...Generator.getReturnActionPayload(state, { ...payload, ...injectActionPayload }),
          ...toSet,
        }
      },
    };
    result[`_${functionName}`] = sagaCallback;
    return result;
  },
  makeDeleteObjectModelActions: (model, functionName, sagaCallback, injectActionPayload) => {
    const result = {};
    result[functionName] = (state, { payload }) => {
      const toSet = {
        loadingMap: [...state.loadingMap, functionName],
      };
      return {
        ...Generator.getActionPayload(state, { ...payload, ...injectActionPayload }),
        ...toSet,
      }
    };
    result[`${functionName}Return`] = {
      next: (state, { payload }) => {
        const toSet = {
          loadingMap: state.loadingMap.filter(x => x !== functionName),
        };
        const currentItem = state[model] ? state[model] : {};
        toSet[model] = payload?.result ? {} : currentItem;
        return {
          ...Generator.getReturnActionPayload(state, { ...payload, ...injectActionPayload }),
          ...toSet,
        }
      },
      throw: (state, { payload }) => {
        const toSet = {
          loadingMap: state.loadingMap.filter(x => x !== functionName),
        };
        return {
          ...Generator.getReturnActionPayload(state, { ...payload, ...injectActionPayload }),
          ...toSet,
        }
      },
    };
    result[`_${functionName}`] = sagaCallback;
    return result;
  },
};

export const makeLoading = (keys) => {
  let combineLoading = false;
  keys.forEach((key) => {
    const originSelector = state => state[key];
    const makeSelector = (stateKey) => createSelector(originSelector, state => state[stateKey]);
    const loading = useSelector(makeSelector('loading'));
    if (loading) {
      combineLoading = true;
    }
  });

  return combineLoading;
};

export default store;